/**
 * "Me" API hooks — /api/me, avatar upload/delete.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useAuthStore, type CurrentUser } from '../../store/auth.js';

export const meKeys = {
  all: ['me'] as const,
  detail: () => [...meKeys.all, 'detail'] as const,
};

/** Fetch the current authenticated user. Errors when not authenticated. */
export function useMe() {
  return useQuery({
    queryKey: meKeys.detail(),
    queryFn: () => api.get<CurrentUser>('/api/me'),
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: (formData: FormData) =>
      api.upload<{ ok: boolean; pictureUrl: string }>('/api/me/avatar', formData),
    onSuccess: async (result) => {
      // Keep AuthGate cache + zustand in sync so Layout avatar updates immediately.
      await qc.invalidateQueries({ queryKey: ['me'] });
      await qc.invalidateQueries({ queryKey: ['employees'] });
      const fresh = await qc.fetchQuery({
        queryKey: ['me'],
        queryFn: () => api.get<CurrentUser>('/api/me'),
      });
      setUser(fresh ?? { ...useAuthStore.getState().user!, pictureUrl: result.pictureUrl });
    },
  });
}

export function useDeleteAvatar() {
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: () => api.delete<{ ok: boolean; pictureUrl: null }>('/api/me/avatar'),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] });
      await qc.invalidateQueries({ queryKey: ['employees'] });
      const fresh = await qc.fetchQuery({
        queryKey: ['me'],
        queryFn: () => api.get<CurrentUser>('/api/me'),
      });
      setUser(fresh);
    },
  });
}
